import { Typography } from "@material-ui/core"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useContext, useEffect, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { lessonAnalytics } from "~/actions/lessons"
import AnalyticsTable from "~/components/Analytics/AnalyticsTable"
import BasicSortHeader from "~/components/Analytics/BasicSortHeader"
import LearningCuesContext from "~/contexts/LearningCues"
import LessonContext from "~/contexts/Lesson"
import usePermissionsRedirect from "~/hooks/usePermissionsRedirect"
import Page from "~/pages/Page"
import Router from "~/router"

import Box from "../../../../../../components/Common/Box"
import useStyles from "./styles"

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.user_id, {
    user_id: "user_id",
    header: ({ column }) => <BasicSortHeader column={column} label="User ID" />,
    id: "user_id",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Username" />
    ),
    id: "username",
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="First Name" />
    ),
    id: "first_name",
    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Name" />
    ),
    id: "last_name",
  }),
  columnHelper.accessor((row) => row.email, {
    email: "email",
    header: ({ column }) => <BasicSortHeader column={column} label="Email" />,
    id: "email",
  }),
  columnHelper.accessor((row) => row.test_user, {
    test_user: "test_user",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Test User" />
    ),
    id: "test_user",
  }),
  columnHelper.accessor((row) => row.lesson_started, {
    lesson_started: "lesson_started",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Lesson Start Date" />
    ),
    id: "lesson_started",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.lesson_started === null
            ? "-"
            : dayjs(props.row.original.lesson_started).format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.lesson_finished, {
    lesson_finished: "lesson_finished",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Lesson Completion Date" />
    ),
    id: "lesson_finished",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.lesson_finished === null
            ? "-"
            : dayjs(props.row.original.lesson_finished).format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_progress, {
    course_progress: "last_progress",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Progress" />
    ),
    id: "last_progress",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.last_progress === null
            ? "-"
            : dayjs(props.row.original.last_progress)?.format(
                "MM/DD/YYYY H:mm",
              )}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.course_name, {
    course_name: "course_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Course Name" />
    ),
    id: "course_name",
  }),
  columnHelper.accessor((row) => row.lesson_id, {
    lesson_id: "lesson_id",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Lesson ID" />
    ),
    id: "lesson_id",
  }),
  columnHelper.accessor((row) => row.course_id, {
    course_id: "course_id",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Course ID" />
    ),
    id: "course_id",
  }),
  columnHelper.accessor((row) => row.finished_knowledge_checks, {
    finished_knowledge_checks: "finished_knowledge_checks",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Finished Knowledge Checks" />
    ),
    id: "finished_knowledge_checks",
  }),

  columnHelper.accessor((row) => row.skipped, {
    skipped: "skipped",
    header: ({ column }) => <BasicSortHeader column={column} label="Skipped" />,
    id: "skipped",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.skipped ? "Yes" : "No"}
        </Typography>
      )
    },
  }),

  columnHelper.accessor((row) => row.assessment_attempts, {
    assessment_attempts: "assessment_attempts",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Assesment Attempts" />
    ),
    id: "assessment_attempts",
  }),
  columnHelper.accessor((row) => row.passed_assessment, {
    passed_assessment: "passed_assessment",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Passed Assessment" />
    ),
    id: "passed_assessment",
  }),
  columnHelper.accessor((row) => row.best_assessment_score, {
    best_assessment_score: "best_assessment_score",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Best Score" />
    ),
    id: "best_assessment_score",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.best_assessment_score === null
            ? "-"
            : props.row.original.best_assessment_score}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.time_on_lesson, {
    time_on_lesson: "time_on_lesson",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Time on Lesson" />
    ),
    id: "time_on_lesson",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.time_on_lesson === null
            ? "-"
            : props.row.original.time_on_lesson}
        </Typography>
      )
    },
  }),
  columnHelper.accessor((row) => row.version, {
    version: "version",
    header: ({ column }) => <BasicSortHeader column={column} label="Version" />,
    id: "version",
  }),
]

const Analytics = ({ routes = [], ...rest }) => {
  usePermissionsRedirect("/lessons", "analytics")

  const { analyticsMutation, downloadAnalyticsMutation, lesson } =
    useContext(LessonContext)
  const [knowledgeCheckCount, setKnowledgeCheckCount] = useState(0)

  const [currentPage, setCurrentPage] = useState(0)
  const [searchValue, setSearchValue] = useState("")
  const [pageSize, setPageSize] = useState(50)
  const { learningCuesQuery, resetLearningCues } =
    useContext(LearningCuesContext)

  useEffect(() => {
    setKnowledgeCheckCount(
      learningCuesQuery?.data?.filter((x) => x.knowledgeCheck)?.length,
    )
  }, [learningCuesQuery])

  const classes = useStyles()

  let dataFetch = {
    searchValue: searchValue.length === 0 ? null : searchValue,
    limit: pageSize,
  }

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["users", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await lessonAnalytics(lesson?.id, {
          ...dataFetch,
          offset: pageParam,
        })
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset
        return nextPageData
      },
    })

  useEffect(() => {
    refetch()
  }, [lesson?.id])

  return (
    <Page>
      <Box padding="30px">
        {/* <AnalyticsFilters type={"lessons"} /> */}
        <AnalyticsTable
          columns={columns}
          mutation={analyticsMutation}
          downloadMutation={downloadAnalyticsMutation}
          type={"lesson"}
          contentData={{ ...lesson, count: knowledgeCheckCount }}
          initialSortString={"lesson_finished"}
          data={data}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
          hasNextPage={hasNextPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
        <Router routes={routes} {...rest} />
      </Box>
    </Page>
  )
}

Analytics.propTypes = {
  routes: PropTypes.array,
}

export default Analytics
